import {
    TAutoLayout,
    TColProps,
    TContainer,
    TContainerKey,
    TContainerResponsive,
    TGutterValues,
    TResponsive,
    TResponsiveGutter
} from './types'
import { BREAKPOINTS, MAXIMUM_COL, CONTAINER } from './constants'

const createRowCssString = (gutter: [number, number]): string => {
    if (gutter[0] === 0 && gutter[1] === 0) return ''
    const rowGap = gutter[0]
    const colGap = gutter[1]
    return `
        margin-left: calc(-0.5 * ${colGap}px);
        margin-right: calc(-0.5 * ${colGap}px);
        margin-top: calc(-1 * ${rowGap}px);
      `
}

const getGapFromGutter = (gutter?: TGutterValues | TResponsiveGutter): [number, number] => {
    if (!gutter) return [0, 0]
    if (typeof gutter === 'number') {
        return [gutter, gutter]
    }
    return gutter as [number, number]
}

export const renderCssStringOfRow = (gutter: TGutterValues | TResponsiveGutter): string => {
    let gap: [number, number] | null = null
    let responsive: TResponsiveGutter | null = null
    if (typeof gutter === 'object' && !Array.isArray(gutter)) {
        responsive = gutter as TResponsiveGutter
    } else {
        gap = getGapFromGutter(gutter)
    }
    if (gap) {
        return createRowCssString(gap)
    }
    if (responsive) {
        let cssString = ''
        Object.keys(responsive).map((breakpoint: TResponsive) => {
            if (breakpoint === 'xs') {
                cssString += createRowCssString(getGapFromGutter(responsive?.[breakpoint]))
            }
            cssString += `
                @media screen and (min-width: ${BREAKPOINTS[breakpoint]}px) {
                  ${createRowCssString(getGapFromGutter(responsive?.[breakpoint]))}
                }
            `
        })
        return cssString
    }
    return ''
}

const renderGap = (colGap: number, rowGap: number) => {
    return `
        ${colGap > 0 ? `padding-left: calc(0.5 * ${colGap}px);` : ''}
        ${colGap > 0 ? `padding-right: calc(0.5 * ${colGap}px);` : ''}
        ${rowGap > 0 ? `padding-top: ${rowGap}px;` : ''}
`
}

const renderAutoLayoutString = (type: TAutoLayout): string => {
    if (type === 'auto') {
        return 'flex: 0 0 auto;max-width:none;'
    }
    return `flex: 1 1 0;max-width:100%;`
}

const createColCssString = (gutter: [number, number], col?: number | TColProps | TAutoLayout): string => {
    let colSpan = 0
    let colOffset = 0
    let colOrder = 0
    const rowGap = gutter[0]
    const colGap = gutter[1]
    if (col) {
        if (typeof col === 'number') {
            colSpan = col
        } else if (col instanceof Object) {
            colSpan = col.span
            colOffset = col.offset || 0
            colOrder = col.order || 0
        } else {
            /*
             * Return css string immediately if column has auto layout
             * */
            return `
                ${renderAutoLayoutString(col)}
                ${renderGap(colGap, rowGap)}
                ${colOrder ? `order: ${colOrder};` : ''}
            `
        }
    }
    const containerWidth = (colSpan / MAXIMUM_COL) * 100
    const containerOffset = colOffset > 0 ? (colOffset / MAXIMUM_COL) * 100 : null
    return `
        ${containerWidth > 0 ? `flex: 0 0 ${containerWidth}%;` : ''}
        ${containerWidth > 0 ? `max-width: ${containerWidth}%;` : ''}
        ${renderGap(colGap, rowGap)}
        ${containerOffset !== null ? `margin-left: ${containerOffset}%;` : ''}
        ${colOrder ? `order: ${colOrder};` : ''}
    `
}

export const renderCssStringOfCol = (
    breakpoints: {
        xs?: number | TColProps | TAutoLayout
        sm?: number | TColProps | TAutoLayout
        md?: number | TColProps | TAutoLayout
        lg?: number | TColProps | TAutoLayout
        xl?: number | TColProps | TAutoLayout
        vnw?: number | TColProps | TAutoLayout
    },
    gutter: TGutterValues | TResponsiveGutter
): string => {
    let gap: [number, number] | null = null
    let responsive: TResponsiveGutter | null = null
    let cssString = ''
    if (typeof gutter === 'object' && !Array.isArray(gutter)) {
        responsive = gutter as TResponsiveGutter
    } else {
        gap = getGapFromGutter(gutter)
    }
    Object.keys(breakpoints).map((breakpoint: TResponsive) => {
        if (breakpoint === 'xs') {
            if (gap) {
                cssString += createColCssString(gap, breakpoints[breakpoint])
                return
            }
            if (responsive) {
                cssString += createColCssString(getGapFromGutter(responsive[breakpoint]), breakpoints[breakpoint])
                return
            }
        }
        if (responsive) {
            if (!breakpoints[breakpoint] && !responsive[breakpoint]) return
            cssString += `
                @media screen and (min-width: ${BREAKPOINTS[breakpoint]}px) {
                    ${createColCssString(getGapFromGutter(responsive[breakpoint]), breakpoints[breakpoint])}
                }
            `
            return
        }
        if (breakpoints[breakpoint]) {
            cssString += `
                @media screen and (min-width: ${BREAKPOINTS[breakpoint]}px) {
                    ${createColCssString([0, 0], breakpoints[breakpoint])}
                }
            `
        }
    })
    return cssString
}

export const renderCssStringOfContainer = (container?: TContainer): string => {
    let cssString = ''
    let containerKey: TContainerKey | undefined
    let padding = 0
    if (container) {
        containerKey = Object.keys(container)[0] as TContainerKey
        if (containerKey) {
            if (containerKey !== 'responsive') {
                padding = container[containerKey] || 0
                cssString = `
                  width: 100%;
                  max-width: ${
                      containerKey === 'full'
                          ? CONTAINER[containerKey]
                          : `${Number(CONTAINER[containerKey]) + padding * 2}px`
                  };
                  padding: 0 ${padding}px;
                  margin: 0 auto;
                `
            } else {
                /*
                 * When container has responsive, the responsive is always has at least 1 item (xs is required).
                 * So that, i can make type convention containerResponsive as TContainerResponsive
                 * */
                const containerResponsive = container[containerKey] as TContainerResponsive
                Object.keys(containerResponsive).forEach((key: TResponsive) => {
                    const containerResponsiveItem = containerResponsive[key]
                    if (containerResponsiveItem) {
                        const size = containerResponsiveItem.size
                        padding = containerResponsiveItem.offset || 0
                        if (key === 'xs') {
                            cssString += `
                                width: 100%;
                                max-width: ${
                                    size === 'full' ? CONTAINER[size] : `${Number(CONTAINER[size]) + padding * 2}px`
                                };
                                padding: 0 ${padding}px;
                                margin: 0 auto;
                            `
                            return
                        }
                        cssString += `
                            width: 100%;
                            @media screen and (min-width: ${BREAKPOINTS[key]}px) {
                                max-width: ${
                                    size === 'full' ? CONTAINER[size] : `${Number(CONTAINER[size]) + padding * 2}px`
                                };
                                ${padding ? `padding: 0 ${padding}px;` : ''}
                            }
                        `
                    }
                })
            }
        }
    }
    return cssString
}
